import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Operations → Cancel Contributions / Distributions → Cancel Contribution
 * (One-Time) / Cancel Contribution (Recurring) / Cancel Distribution
 * (One-Time) / Cancel Distribution (Recurring).
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screens "Cancel One-Time / Recurring
 * Contributions" (Cancel_One_Time_Recurring_Contributions_Screen) and "Cancel
 * One-Time / Recurring Distributions"
 * (Cancel_One_Time_Recurring_Distributions_Screen). The two screens ask the
 * same questions over the same account table; only the screen title differs.
 * For the two Recurring sub-categories the flow also clears the recurring
 * schedule recorded on the chosen sleeve (Cancel_Recurring_Contribution_
 * Distribution_Update). Shared chrome and policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'Cancel Contributions / Distributions',
    subCategory: 'Cancel Contribution (One-Time), Cancel Contribution (Recurring), Cancel Distribution (One-Time) or Cancel Distribution (Recurring)',
    subject: subjectRow('Cancel recurring contribution - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which accounts you can pick later, and the advisor and office are taken from it.']],
    note: (
      <>
        <strong>All four sub-categories ask exactly the same questions.</strong> Pick the one that matches what
        you are cancelling - it is recorded on the case, and the two Recurring choices also clear the recurring
        schedule held in FSC (see What happens next).
      </>
    ),
  }),
  {
    title: 'Give the cancellation date and instructions',
    body: (
      <p style={{ margin: 0 }}>
        The next screen is titled <strong>Cancel One-Time / Recurring Contributions</strong> or{' '}
        <strong>Cancel One-Time / Recurring Distributions</strong>, depending on your Sub-Category. It opens with
        two required boxes: <em>Effective Date of Cancellation</em> and <em>Additional Instructions</em>. Nothing
        else on the screen appears until Additional Instructions has something in it. It becomes the case
        description Operations reads first, so say what is being cancelled - the amount, the frequency and the
        date it was set up to run.
      </p>
    ),
  },
  {
    title: 'Choose the account',
    body: (
      <p style={{ margin: 0 }}>
        An <strong>Accounts</strong> table then appears. Pick exactly one row - it is required, and you cannot pick
        more than one. It shows Name, Account Information, Model and Balance. If the request covers more than one
        account, submit a separate case for each.
      </p>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice under the table - it governs timing and what the teams can and
        cannot do. Select <em>I have read, understand, and agree to the above information</em> under
        Acknowledgement and click Next. FSC creates the case and opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

// Every question on the Cancel One-Time / Recurring Contributions and
// Cancel One-Time / Recurring Distributions screens (identical), in order.
const QUESTIONS = [
  {
    q: 'Effective Date of Cancellation',
    when: 'Always - this is the first question',
    note: 'Required. The date the contribution or distribution should stop. It is recorded on the case alongside your instructions.',
  },
  {
    q: 'Additional Instructions',
    when: 'Always',
    note: 'Required. This becomes the case description. The account table, the notice and the Acknowledgement stay hidden until you type something here.',
  },
  {
    q: 'Accounts',
    when: 'Once Additional Instructions has been filled in',
    note: (
      <>
        A table - pick exactly one row. Columns: Name, Account Information, Model, Balance. It lists the
        household&rsquo;s active sleeves with a balance of zero or more. Cancelled sleeves (names ending in CNX)
        and the Contribution and Distribution holding sleeves (names ending in &ldquo;Contribution&rdquo; or
        &ldquo;Distribution&rdquo;) are left out.
      </>
    ),
  },
  {
    q: 'Acknowledgement',
    when: 'Once Additional Instructions has been filled in, below the Operations and Trading notice',
    options: ['I have read, understand, and agree to the above information'],
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'Cancel Contributions / Distributions'),
  {
    h: "The account I expected isn't in the list",
    p: [
      'The Accounts table lists sleeves, not just the account as a whole - you will see rows like 0989-022356_DFA TM 100/0. Pick the sleeve the contribution or distribution was set up on.',
      'Inactive sleeves, sleeves with a negative balance, cancelled sleeves ending in CNX, and the Contribution and Distribution holding sleeves are filtered out.',
    ],
  },
  {
    h: "The table and notice haven't appeared",
    p: ['They stay hidden until the Additional Instructions box has something in it.'],
  },
  {
    h: 'I need to cancel on more than one account',
    p: ['The table only accepts one row. Submit one case per account.'],
  },
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function CancelContributionsDistributions() {
  return (
    <GuidePage
      title="Cancel a Contribution or Distribution"
      icon="x-circle"
      path="Operations → Cancel Contributions / Distributions → One-Time or Recurring Contribution or Distribution"
      minutes={3}
      intro="How to ask the Prosperity Operations team to cancel a one-time or recurring contribution or distribution - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The form is short, but Operations works from what you write.</Para>
        <Bullets items={[
          <><strong>Which kind of request it is</strong> - a contribution or a distribution, and one-time or recurring. Each is its own Sub-Category.</>,
          <><strong>The account it was set up on</strong>, under the client&rsquo;s household. You pick it from a list, so you do not need the account number memorised.</>,
          <><strong>The effective date of the cancellation.</strong></>,
          <><strong>The details of what is being cancelled</strong> - amount, frequency and the date it was due to run - for the Additional Instructions box.</>,
        ]} />
        <Callout>
          One case covers one account. If the client has contributions or distributions to cancel on several
          accounts, submit a case for each.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>The contribution and distribution screens ask the same questions in the same order.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext
        team="operations"
        after={(
          <Callout tone="warn">
            <strong>Recurring cancellations take effect in FSC straight away.</strong> For Cancel Contribution
            (Recurring) and Cancel Distribution (Recurring), submitting the case clears the recurring schedule
            recorded on the sleeve you picked - amount, frequency, start and end dates and tax withholding - and
            notes on the sleeve who cancelled it, when, and the case number. Make sure you picked the right sleeve.
          </Callout>
        )}
      />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It does not cover the custodian&rsquo;s own cancellation
        cutoffs, or whether a contribution or distribution already in flight can still be stopped.
      </Footnote>
    </GuidePage>
  );
}
